import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type AlertEvent, type EnvironmentHealthView, type HealthLevel, type LiveInspection } from '../api';
import { Callout } from './primitives';
import { AttentionFeed } from './status/AttentionFeed';
import { LoadingRows } from './ui';
import { PanelCard } from './project/ui';
import { actionLabel } from './operation/actionLabels';
import { useResource } from '../hooks';
import { targetPath, type SectionPath } from './project/operationalHealth';

/**
 * EnvironmentReadiness answers one question for the operator: is this
 * environment safe to leave alone right now, and if not, what exactly needs
 * doing. The ledger rows — agent, HTTPS, backups from the record, host and
 * server state inspected live — come from the server's one health
 * derivation (GET /environments/{id}/health?live=1, CO-95); this file only
 * renders them and attaches the two actions that run from here (request a
 * certificate, free up space). Open alerts and the server's differences
 * from the record follow as "what is wrong → what to do".
 */

export type Level = 'ok' | 'warning' | 'critical' | 'unknown';

export interface LedgerRow {
  key: string;
  label: string;
  level: Level;
  fact: string;
  live?: boolean;
  action?: { label: string; onClick?: () => void; to?: string; busy?: boolean };
}

const levelWord: Record<Level, string> = {
  ok: 'fine',
  warning: 'needs attention',
  critical: 'needs action now',
  unknown: 'not known',
};

export interface ReadinessState {
  rows: LedgerRow[];
  openAlerts: AlertEvent[];
  findings: LiveInspection['findings'];
  /** The server's level and headline for the environment. */
  level: HealthLevel;
  headline: string;
  health: EnvironmentHealthView | null;
  error: Error | null;
  loading: boolean;
  /**
   * The server inspection is still running: the rows shown are the recorded
   * ones, and the host and server-state rows join when it answers.
   */
  inspecting: boolean;
  checkedAt?: string;
  /** What a started action did, with the run it started (CO-184: a link, not an id). */
  notice: { text: string; to: string } | null;
  actionError: Error | null;
  busy: string | null;
  reload: () => void;
  resolve: (alert: AlertEvent) => Promise<void>;
}

/**
 * useEnvironmentReadiness reads the environment's health with a live
 * inspection of its server. Both the full panel and the Overview's
 * summary consume it, so the two can never disagree.
 */
export function useEnvironmentReadiness(
  environmentId: string,
  sectionPath: SectionPath,
  reloadKey: unknown = 0,
  /** False while no screen shows the ledger: the live inspection then does not run. */
  enabled = true,
  /**
   * The environment's recorded health, which the project already has. The
   * ledger shows its rows at once while the live inspection (slow: it asks the
   * server itself) is still running, instead of nothing until it answers.
   */
  recorded?: EnvironmentHealthView | null,
): ReadinessState {
  const [reloadToken, setReloadToken] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; to: string } | null>(null);
  const [actionError, setActionError] = useState<Error | null>(null);

  // An empty id means the project has no environment selected yet — the
  // ledger stays unknown rather than asking the API about nothing.
  const health = useResource(
    () => (environmentId ? api.getEnvironmentHealth(environmentId, { live: true }) : Promise.resolve(null)),
    [environmentId, reloadToken, reloadKey],
    environmentId,
    // The live read asks the agent to inspect the server, which takes a while
    // and costs the server something: a cached ledger is good for a minute, and
    // it is not re-run by deployment progress events (see liveRefresh.ts).
    { key: `env:${environmentId}:readiness-health`, staleMs: 60_000, enabled },
  );
  const alerts = useResource(
    () => (environmentId ? api.listEnvironmentAlerts(environmentId) : Promise.resolve({ alerts: [] })),
    [environmentId, reloadToken, reloadKey],
    environmentId, { key: `env:${environmentId}:alerts`, enabled },
  );

  async function runAction(key: string, fn: () => Promise<{ execution: { id: string } }>, done: string) {
    setBusy(key);
    setActionError(null);
    setNotice(null);
    try {
      const res = await fn();
      setNotice({ text: done, to: sectionPath('infrastructure', { run: res.execution.id }) });
      setReloadToken((v) => v + 1);
    } catch (cause) {
      setActionError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(null);
    }
  }

  async function resolve(alert: AlertEvent) {
    setBusy(`alert-${alert.id}`);
    setActionError(null);
    try {
      await api.resolveAlert(alert.id);
      setReloadToken((v) => v + 1);
    } catch (cause) {
      setActionError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(null);
    }
  }

  const live = health.data;
  const view = live ?? recorded ?? null;
  const inspecting = enabled && live === null && health.error === null;
  const rows: LedgerRow[] = useMemo(() => {
    if (!view) return [];
    // A certificate the agent can request is requested from here; the
    // server named which hostnames are in that state.
    const requestable = (view.certificates ?? []).some((c) => c.state === 'failed' || c.state === 'expired' || c.state === 'pending');
    const reclaimable = view.live?.reclaimable_bytes ?? 0;
    return view.readiness.map((row) => {
      const out: LedgerRow = {
        key: row.key,
        label: row.label,
        level: row.level,
        fact: row.fact,
        live: row.live,
        action: row.action ? { label: row.action.label, to: targetPath(row.action.target, sectionPath, environmentId) } : undefined,
      };
      if (row.key === 'https' && row.level !== 'ok' && requestable) {
        out.action = {
          label: busy === 'cert' ? 'Requesting' : 'Request certificate',
          onClick: () => runAction('cert', () => api.issueEnvironmentCertificate(environmentId), 'Certificate requested.'),
          busy: busy === 'cert',
        };
      }
      if (row.key === 'host' && row.level !== 'ok' && reclaimable > 0) {
        out.action = {
          label: busy === 'cleanup' ? 'Freeing' : 'Free up space',
          onClick: () => runAction('cleanup', () => api.cleanupEnvironment(environmentId), 'Cleanup started.'),
          busy: busy === 'cleanup',
        };
      }
      return out;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, busy, environmentId, sectionPath]);

  return {
    rows,
    openAlerts: alerts.data?.alerts ?? [],
    findings: view?.live?.findings ?? [],
    level: view?.level ?? 'unknown',
    headline: view?.headline ?? (health.loading ? 'Checking the environment…' : health.error ? 'The environment could not be checked.' : ''),
    health: view ?? null,
    error: health.error,
    loading: health.loading,
    inspecting,
    checkedAt: view?.live?.checked_at ?? view?.checked_at,
    notice,
    actionError,
    busy,
    reload: () => setReloadToken((v) => v + 1),
    resolve,
  };
}

const panelLevel: Record<HealthLevel, Level> = { healthy: 'ok', degraded: 'warning', critical: 'critical', unknown: 'unknown' };

export function ReadinessPanel({ environmentId, state }: { environmentId: string; state: ReadinessState }) {
  const { rows, openAlerts, findings, level, headline, loading, inspecting, checkedAt, notice, actionError, busy } = state;
  // One failure is told once: as the callout below, with its cause and Retry
  // (PR27). The hook's own "could not be checked" headline is not a second telling.
  const failed = state.error !== null;
  const showHeadline0 = !(failed && rows.length === 0) && headline !== '';
  // A healthy ledger needs no sentence: the rows are the statement, and the
  // server's "everything is fine" headline would only repeat them. Any other
  // level keeps the server's headline, and a check in flight keeps its own.
  const quiet = level === 'healthy' && !failed && !loading && !inspecting;
  const showHeadline = showHeadline0 && !(quiet && !checkedAt);

  return (
    <PanelCard className="readiness" labelledBy={`readiness-${environmentId}`} tone={panelLevel[level]} testId="readiness-panel">
      <div className="readiness__head">
        <div>
          <h2 id={`readiness-${environmentId}`} tabIndex={-1}>Readiness</h2>
          {showHeadline ? (
            <p className="type-description" data-testid="readiness-headline">
              {quiet ? null : headline}
              {inspecting && rows.length > 0 ? (
                <span className="muted" data-testid="readiness-inspecting"> · checking the server…</span>
              ) : checkedAt && !loading ? (
                <span className="muted">{quiet ? 'Checked' : ' · checked'} {new Date(checkedAt).toLocaleTimeString()}</span>
              ) : null}
            </p>
          ) : null}
        </div>
        <div className="pill-list">
          {failed ? null : (
            <button type="button" className="button button--secondary" onClick={state.reload} disabled={loading}>
              Check again
            </button>
          )}
          <a className="button button--secondary" href={api.supportBundleURL(environmentId)} download>
            Download support bundle
          </a>
        </div>
      </div>

      {notice ? (
        <Callout tone="success" testId="readiness-notice">
          {notice.text}{' '}
          <Link to={notice.to}>
            {actionLabel.viewDetails}
            <span className="visually-hidden"> of the run it started</span>
          </Link>
        </Callout>
      ) : null}
      {actionError ? <Callout tone="danger">{actionError.message}</Callout> : null}
      {failed ? (
        <Callout
          tone="danger"
          title="The environment could not be checked"
          testId="readiness-error"
          actions={
            <button type="button" className="button button--secondary button--small" onClick={state.reload} disabled={loading}>
              {actionLabel.retry}
            </button>
          }
        >
          {state.error!.message}
          {rows.length > 0 ? ' The checks below are the last recorded ones.' : ''}
        </Callout>
      ) : null}

      {loading && rows.length === 0 ? <LoadingRows label="Checking the environment" rows={5} rowHeight="28px" /> : null}
      <ol className="ledger" aria-label="Readiness checks">
        {rows.map((row) => (
          <li key={row.key} className={`ledger__row ledger__row--${row.level}`} data-testid={`readiness-${row.key}`}>
            <span className="ledger__mark" aria-hidden="true" />
            <span className="ledger__label">
              {row.label}
              <span className="visually-hidden">: {levelWord[row.level]}</span>
              {row.live ? <span className="ledger__live">live</span> : null}
            </span>
            <span className="ledger__fact">{row.fact}</span>
            {row.action ? (
              row.action.to ? (
                <Link className="button button--secondary ledger__action" to={row.action.to}>
                  {row.action.label}
                </Link>
              ) : (
                <button type="button" className="button button--secondary ledger__action" onClick={row.action.onClick} disabled={row.action.busy}>
                  {row.action.label}
                </button>
              )
            ) : null}
          </li>
        ))}
      </ol>

      {openAlerts.length > 0 || findings.length > 0 ? (
        <div className="readiness__attention">
          <h3>Needs attention</h3>
          <AttentionFeed
            label="Needs attention"
            testId="readiness-attention"
            limit={4}
            items={[
              ...openAlerts.map((a) => ({
                key: a.id,
                severity: a.severity,
                title: a.title,
                summary: a.message || undefined,
                when: { label: a.occurrences > 1 ? `${a.occurrences} times, last` : 'Last seen', at: a.last_seen_at },
                action: { label: busy === `alert-${a.id}` ? 'Resolving' : 'Mark resolved', onClick: () => state.resolve(a), busy: busy === `alert-${a.id}` },
              })),
              ...findings.map((f, i) => ({
                key: `${f.kind}-${f.subject}-${i}`,
                severity: f.severity === 'critical' ? ('critical' as const) : ('warning' as const),
                title: `${f.kind === 'release' ? 'Release' : f.kind === 'service' ? `Service ${f.subject}` : f.kind === 'volume' ? 'Data volume' : 'Generated file'}: expected ${f.expected}, found ${f.observed}`,
                summary: f.advice,
              })),
            ]}
          />
        </div>
      ) : null}
    </PanelCard>
  );
}
